import { MapContainer, TileLayer, GeoJSON, Marker, useMapEvents, useMap } from "react-leaflet";
import { useEffect, useMemo } from "react";
import L from "leaflet";

const UK_CENTER = [54.2, -4.5];

const CLASS_EMPHASIS = {
  sub250: { fill: 0.75, weight: 0 },
  a2: { fill: 1.0, weight: 0 },
  heavy: { fill: 1.4, weight: 0.5 },
};

const makeZoneStyle = (weightClass, basemap) => (feature) => {
  const p = feature.properties;
  const emph = CLASS_EMPHASIS[weightClass] || CLASS_EMPHASIS.a2;
  const boost = basemap === "satellite" ? 0.35 : 0;
  const base = { color: p.color, weight: 1.8 + emph.weight, fillColor: p.color, opacity: 0.9 };
  const t = p.zone_type;
  let fillOpacity = 0.2;
  let dashArray = null;
  if (t === "FRZ" || t === "PROHIBITED") { fillOpacity = 0.22; base.weight += 0.5; }
  else if (t === "RESTRICTED") fillOpacity = 0.18;
  else if (t === "HIRTA" || t === "DANGER") { fillOpacity = 0.13; dashArray = "8 6"; }
  else if (t === "COUNCIL") fillOpacity = 0.2;
  else fillOpacity = 0.15;
  return { ...base, fillOpacity: Math.min(fillOpacity * emph.fill + boost * 0.1, 0.5), ...(dashArray ? { dashArray } : {}) };
};

const onEachZone = (feature, layer) => {
  const p = feature.properties;
  const permit = p.permit_url
    ? `<a href="${p.permit_url}" target="_blank" rel="noreferrer" style="color:#3399ff">Request council permit →</a>`
    : "";
  layer.bindPopup(
    `<div style="max-width:260px">
      <div style="font-weight:600;font-size:14px;margin-bottom:4px;color:#fff">${p.name}</div>
      <div style="font-size:12px;color:${p.color};font-weight:700;letter-spacing:0.08em;margin-bottom:6px">${p.restriction_level.replaceAll("_", " ")} · ${p.source_agency}</div>
      <div style="font-size:13px;color:#d4d4d8;line-height:1.45;max-height:120px;overflow:auto">${p.description}</div>
      ${permit}
    </div>`
  );
};

const ClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const FlyToTarget = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lng], target.zoom || 13, { duration: 1.2 });
  }, [target, map]);
  return null;
};

const MapGrabber = ({ mapRef }) => {
  const map = useMap();
  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);
  return null;
};

const makePinIcon = (noFly) =>
  L.divIcon({
    className: "",
    html: `<div class="pin-marker ${noFly ? "no-fly" : ""}"><div class="pin-dot"></div></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });

const spotIcon = L.divIcon({
  className: "",
  html: `<div class="spot-marker"><svg width="12" height="12" viewBox="0 0 24 24" fill="#0a0a0a"><path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.2 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8z"/></svg></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const BASEMAPS = {
  satellite: [
    { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: "&copy; Esri, Maxar, Earthstar Geographics" },
    { url: "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}", attribution: "" },
  ],
  dark: [
    { url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", attribution: '&copy; <a href="https://carto.com/">CARTO</a> &copy; OpenStreetMap' },
  ],
  streets: [
    { url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", attribution: '&copy; <a href="https://carto.com/">CARTO</a> &copy; OpenStreetMap' },
  ],
};

export const MapView = ({ zones, zonesKey, onMapClick, selectedPoint, flyTarget, status, weightClass, spots = [], onSpotSelect, basemap = "satellite", mapRef }) => {
  const zoneStyle = useMemo(() => makeZoneStyle(weightClass, basemap), [weightClass, basemap]);
  const pinIcon = useMemo(() => makePinIcon(status === "STRICT_NO_FLY"), [status]);

  const spotMarkers = useMemo(
    () =>
      spots.map((s) => (
        <Marker
          key={s.name}
          position={[s.lat, s.lng]}
          icon={spotIcon}
          eventHandlers={{ click: () => onSpotSelect && onSpotSelect(s) }}
        />
      )),
    [spots, onSpotSelect]
  );

  const pinMarker = useMemo(
    () => (selectedPoint ? <Marker position={[selectedPoint.lat, selectedPoint.lng]} icon={pinIcon} /> : null),
    [selectedPoint, pinIcon]
  );

  return (
    <div className="absolute inset-0 z-0" data-testid="map-container" aria-label="Interactive Drone Map">
      <MapContainer center={UK_CENTER} zoom={6} zoomControl={false} preferCanvas={true} className="h-full w-full">
        <MapGrabber mapRef={mapRef} />
        {BASEMAPS[basemap].map((t, i) => (
          <TileLayer key={`${basemap}-${i}`} url={t.url} attribution={t.attribution} />
        ))}
        {zones && (
          <GeoJSON key={zonesKey} data={zones} style={zoneStyle} onEachFeature={onEachZone} />
        )}
        {spotMarkers}
        {pinMarker}
        <ClickHandler onMapClick={onMapClick} />
        <FlyToTarget target={flyTarget} />
      </MapContainer>
    </div>
  );
};
